/**
 * Shared filter constants + helpers for Operations list pages
 * (Solution Analyses, Hints, Concept Lab, …).
 *
 * Every operations list shares three filters (dateRange / subject / email);
 * page-specific filters are passed to <OperationsFilterBar> as `extraDefs`.
 */

import type { FilterDef, RowMenuItem } from '@shared-ui/components';
import type { UserSearchOption } from '@shared-ui/user-picker';

/** The slice of a list row the user filter needs. Every operations list row
 *  carries the same optional `user` shape, so one helper serves them all. */
interface RowWithUser {
  user?: { id?: string; name?: string; email?: string } | null;
}

/**
 * The users offered in the filter before anything is typed — those on the rows
 * currently on screen, deduped by user id — so the common pick costs no request.
 * Typing runs the backend search instead.
 *
 * Deduped and keyed on the ID, not the email: a school roster account has no
 * email at all, so keying on one dropped every student of a school tenant from
 * the suggestions and made the filter unusable for exactly the users an admin
 * looking at a school is there to see.
 *
 * Lives here rather than in each list: the derivation is identical everywhere,
 * and it was already copied three times before this existed.
 */
export function userSuggestionsFromRows(rows: readonly RowWithUser[]): UserSearchOption[] {
  const seen = new Set<string>();
  const out: UserSearchOption[] = [];
  for (const row of rows) {
    const userId = row.user?.id;
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    out.push({ userId, email: row.user?.email ?? '', name: row.user?.name });
  }
  return out;
}

export const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '3d', label: '< 3 days' },
  { value: '7d', label: '< 7 days' },
  { value: 'all', label: 'All' },
];

export const SUBJECT_OPTIONS = [
  { value: 'maths', label: 'Maths' },
  { value: 'physics', label: 'Physics' },
  { value: 'chemistry', label: 'Chemistry' },
  { value: 'biology', label: 'Biology' },
];

/** The default `dateRange` value used by every operations page. */
export const DEFAULT_DATE_RANGE = '7d';

/** Persona-agent visibility filter, shared across operations pages. Agents are
 *  synthetic dev-only accounts (marked by `agentPersonaId`); hidden by default
 *  so real activity isn't drowned out. */
export const AGENT_OPTIONS = [
  { value: 'hide', label: 'Hide agents' },
  { value: 'only', label: 'Only agents' },
  { value: 'all', label: 'All users' },
];

/** Default `agents` value — hide synthetic agent activity unless asked for. */
export const DEFAULT_AGENTS = 'hide';

/** Shared `agents` filter def for <OperationsFilterBar> extraDefs. `required`
 *  so the select always shows its current value (never an empty placeholder). */
export const AGENTS_FILTER_DEF: FilterDef = {
  key: 'agents', kind: 'select', placeholder: 'Agents', required: true, options: AGENT_OPTIONS,
};

/** Convert a dateRange option to an absolute ISO timestamp (or undefined for 'all'). */
export function getSinceDate(range: string): string | undefined {
  if (range === 'all') return undefined;
  const now = new Date();
  if (range === 'today') now.setHours(0, 0, 0, 0);
  else if (range === '3d') now.setDate(now.getDate() - 3);
  else if (range === '7d') now.setDate(now.getDate() - 7);
  return now.toISOString();
}

/**
 * Persist filters + page to the URL, preserving any other params (e.g. `tab`).
 * `dateRange` is dropped from the URL when it equals the default (keeps URLs clean).
 */
export function syncOperationsFiltersToUrl(
  setSearchParams: (
    setter: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean }
  ) => void,
  filters: Record<string, string>,
  page: number,
  /** Namespace so each operation tab owns its own filter/page params in the one
   *  URL — without it, switching e.g. submissions → hints bleeds stale page/
   *  subject/… across lists. Key `subject` under prefix `hints` → `hints.subject`. */
  prefix = '',
): void {
  const k = (key: string) => (prefix ? `${prefix}.${key}` : key);
  setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    for (const [key, val] of Object.entries(filters)) {
      const keep = key === 'dateRange' ? (val && val !== DEFAULT_DATE_RANGE)
        : key === 'agents' ? (val && val !== DEFAULT_AGENTS)
        : !!val;
      if (keep) params.set(k(key), val);
      else params.delete(k(key));
    }
    if (page > 1) params.set(k('page'), String(page));
    else params.delete(k('page'));
    return params;
  }, { replace: true });
}

/** Read one operations filter/page param, honouring the same namespace prefix
 *  as {@link syncOperationsFiltersToUrl}. */
export function readOperationsParam(searchParams: URLSearchParams, key: string, prefix = ''): string {
  return searchParams.get(prefix ? `${prefix}.${key}` : key) ?? '';
}

/** The always-present operations filter defs, in canonical order. */
export const COMMON_OPERATIONS_FILTER_DEFS: { dateRange: FilterDef; subject: FilterDef; email: FilterDef } = {
  dateRange: { key: 'dateRange', kind: 'select', placeholder: 'Date Range', required: true, options: DATE_RANGE_OPTIONS },
  subject: { key: 'subject', kind: 'select', placeholder: 'All Subjects', options: SUBJECT_OPTIONS },
  email: { key: 'email', kind: 'text', placeholder: 'User email' },
};

/**
 * Right-click menu items that narrow an operations list to the row under the
 * pointer.
 *
 * Shared so every operations table offers the same two cuts in the same words,
 * and so a list only offers what it can actually apply: pass `questionId` only
 * where a question filter exists, `userId` only where a user filter does.
 * Offering a filter a list cannot honour is worse than offering none.
 */
export function operationsRowMenuItems(params: {
  /** The row's user id — what the filter applies. */
  userId?: string;
  /** How to name them in the menu: their email, or their name where a roster
   *  account has no email. */
  userLabel?: string;
  questionId?: string;
  appliedUserId?: string;
  appliedQuestionId?: string;
  /** Applies the narrowing — normally sets state, resets to page 1 and syncs the URL. */
  apply: (next: { userId?: string; questionId?: string }) => void;
}): RowMenuItem[] {
  const { userId, userLabel, questionId, appliedUserId, appliedQuestionId, apply } = params;
  const items: RowMenuItem[] = [];

  if (userId) {
    items.push({
      key: 'user',
      label: `Filter to ${userLabel || 'this user'}`,
      disabled: appliedUserId === userId,
      onSelect: () => apply({ userId }),
    });
  }
  if (questionId) {
    items.push({
      key: 'question',
      label: 'Filter to this question',
      disabled: appliedQuestionId === questionId,
      onSelect: () => apply({ questionId }),
    });
  }
  if (userId && questionId) {
    items.push({
      key: 'both',
      label: 'Filter to this user AND question',
      disabled: appliedUserId === userId && appliedQuestionId === questionId,
      onSelect: () => apply({ userId, questionId }),
    });
  }
  if (items.length > 0) {
    const label = questionId
      ? (userId ? 'Clear user and question filters' : 'Clear question filter')
      : 'Clear user filter';
    items.push({
      key: 'clear',
      label,
      separatorBefore: true,
      disabled: !appliedUserId && !appliedQuestionId,
      // Empty string, not undefined: these lists treat '' as "no filter" and
      // undefined would leave the current value in place.
      onSelect: () => apply({ userId: '', ...(questionId ? { questionId: '' } : {}) }),
    });
  }
  return items;
}
